import {Helmet} from '@dr.pogodin/react-helmet';
import {BreadcrumbType, Form, FormField, FormSubmitHandler} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetEmployeeProfessionQuery,
  useGetEmployeeProfessionTypesQuery,
  usePatchEmployeeProfessionMutation,
  usePostEmployeeProfessionMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {
  EmployeeProfessionsForm,
  EmployeeProfessionsFormSchema,
} from './types/EmployeeProfessionsFormSchema';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('entity.employeeProfessions.labels.title'),
    href: settingsRoutes.employeeProfessions,
  },
];

export function EmployeeProfessionsDetail() {
  const navigate = useNavigate();
  const {id} = useParams();

  const [postEmployeeProfession] = usePostEmployeeProfessionMutation();
  const [patchEmployeeProfession] = usePatchEmployeeProfessionMutation();
  const {data, isError, isLoading} = useGetEmployeeProfessionQuery(
    {id: id ?? ''},
    {skip: isNil(id)}
  );
  const {data: professionTypes, isLoading: areProfessionTypesLoading} =
    useGetEmployeeProfessionTypesQuery();

  const professionTypeOptions =
    professionTypes
      ?.filter(isNotNil)
      .map((professionType) => ({label: professionType.label, value: professionType.key})) ?? [];

  const handleSubmit: FormSubmitHandler<EmployeeProfessionsForm> = async (
    {description, type},
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const request = isNil(id)
      ? postEmployeeProfession({body: {description, type}})
      : patchEmployeeProfession({body: {description, type}, id});

    await request
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.employeeProfessions);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <>
      <Helmet
        title={
          isNil(id)
            ? i18n.t('entity.employeeProfessions.labels.newProfession')
            : i18n.t('entity.employeeProfessions.labels.title')
        }
      />
      <SettingsTemplate
        header={{
          breadcrumbs,
          title: data?.description ?? i18n.t('entity.employeeProfessions.labels.newProfession'),
        }}
        isLoading={isLoading}
        isError={isError}
        isCreating={isNilOrEmpty(id)}
        data-testid={testIds.settings.employeeProfessionsDetail('settingsTemplate')}
      >
        <Form<EmployeeProfessionsForm>
          defaultValues={{
            description: data?.description ?? undefined,
            type: data?.type?.typeKey ?? undefined,
          }}
          experimentalZodSchema={EmployeeProfessionsFormSchema}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <>
              <SettingsSection>
                <VStack spacing={4}>
                  <FormField
                    control={control}
                    type="text"
                    name="description"
                    isRequired
                    label={i18n.t('general.labels.description')}
                    data-testid={testIds.settings.employeeProfessionsDetail('description')}
                  />
                  <FormField
                    control={control}
                    type="choice"
                    name="type"
                    label={i18n.t('general.labels.type')}
                    options={professionTypeOptions}
                    isLoading={areProfessionTypesLoading}
                    data-testid={testIds.settings.employeeProfessionsDetail('type')}
                  />
                </VStack>
              </SettingsSection>
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    onClick: () => navigateWithoutBlocker(settingsRoutes.employeeProfessions),
                    title: id
                      ? i18n.t('general.actions.discardChanges')
                      : i18n.t('general.actions.discard'),
                    variant: 'secondary',
                    'data-testid': testIds.settings.employeeProfessionsDetail('discard'),
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    isDisabled: areProfessionTypesLoading,
                    title: id
                      ? i18n.t('general.actions.saveAndClose')
                      : i18n.t('general.actions.create'),
                    'data-testid': testIds.settings.employeeProfessionsDetail('save'),
                  },
                ]}
              />
            </>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}
